// Promo links (Jeremy 2026-10-11): any book.aluume.com URL can carry ?promo=CODE (partner QR
// codes — Peppers Beacon first). The code is captured on whichever page the guest LANDS on and
// follows them round the site: land on /bathe, book Thermae — it still applies. It is applied at
// checkout through the normal promo box, so the server re-validates it on every charge path
// exactly like a typed code; the stored value is only a convenience, never trusted for price.
//
// Kept in localStorage (not sessionStorage) for PROMO_LINK_TTL_MS so a guest who scans the QR,
// closes the tab and books later that week still gets it. The code's own valid_from/valid_until
// remain the real limit.

export const PROMO_LINK_PARAM = "promo";
export const PROMO_LINK_KEY = "aluume_promo_link";
export const PROMO_LINK_TTL_MS = 7 * 24 * 60 * 60 * 1000;

// Letters, digits, - and _ (every live promo code fits, 2026-10-11). The staff Share link refuses
// a code outside this (isLinkablePromoCode) rather than print a QR the site would ignore.
const CODE_RE = /^[A-Za-z0-9_-]{2,64}$/;

export function isLinkablePromoCode(code: string): boolean {
  return CODE_RE.test(code.trim());
}

type StorageLike = Pick<Storage, "getItem" | "setItem" | "removeItem">;

/** The promo code in a query string, upper-cased — or null if absent / malformed. */
export function promoFromSearch(search: string): string | null {
  const raw = new URLSearchParams(search).get(PROMO_LINK_PARAM)?.trim();
  return raw && CODE_RE.test(raw) ? raw.toUpperCase() : null;
}

export function writePromoLink(store: StorageLike, code: string, now = Date.now()): void {
  store.setItem(PROMO_LINK_KEY, JSON.stringify({ code: code.toUpperCase(), at: now }));
}

/** The stored link code if it is well-formed and inside the TTL; clears a stale/corrupt entry. */
export function readPromoLink(store: StorageLike, now = Date.now()): string | null {
  const raw = store.getItem(PROMO_LINK_KEY);
  if (!raw) return null;
  try {
    const v = JSON.parse(raw) as { code?: unknown; at?: unknown };
    if (typeof v.code === "string" && CODE_RE.test(v.code) && typeof v.at === "number" && now - v.at >= 0 && now - v.at < PROMO_LINK_TTL_MS) {
      return v.code;
    }
  } catch { /* corrupt → cleared below */ }
  store.removeItem(PROMO_LINK_KEY);
  return null;
}

export function clearPromoLink(store: StorageLike): void {
  store.removeItem(PROMO_LINK_KEY);
}

/** Browser helpers — never throw (private mode / storage disabled → no link promo). */
export function readPromoLinkSafe(): string | null {
  try { return readPromoLink(window.localStorage); } catch { return null; }
}
export function clearPromoLinkSafe(): void {
  try { clearPromoLink(window.localStorage); } catch { /* nothing */ }
}
